import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";

const PROJECT_ROUTES = [
  "/projets/car-wash",
  "/projets/cake",
  "/projets/nail-studio",
  "/projets/bien-etre",
  "/projets/bella-napoli",
  "/projets/salon-cote",
  "/projets/depann-eau-pro",
  "/projets/barbier",
];

/**
 * ProjectTopBar — floating pill bar fixed to the top-left of any project page.
 * Provides: back-to-portfolio arrow + "Voir un autre projet" cycler.
 * Deliberately minimal so it never visually competes with each project's own design.
 */
export default function ProjectTopBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Defer animation slightly so it appears after route enter
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const nextProject = () => {
    const idx = PROJECT_ROUTES.indexOf(location.pathname);
    const next = PROJECT_ROUTES[(idx + 1 + PROJECT_ROUTES.length) % PROJECT_ROUTES.length];
    navigate(next);
  };

  return (
    <motion.div
      data-testid="project-topbar"
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: mounted ? 1 : 0, y: mounted ? 0 : -12 }}
      transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
      className="fixed top-5 left-5 right-5 sm:right-auto z-[110] flex items-center gap-2 pointer-events-none"
    >
      <button
        type="button"
        data-testid="project-topbar-back"
        onClick={() => navigate("/portfolio")}
        aria-label="Retour au portfolio"
        className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-black/70 backdrop-blur-lg border border-white/12 px-4 py-2.5 text-white hover:bg-[var(--accent)] hover:text-black hover:border-[var(--accent)] transition-all duration-300 shadow-[0_8px_30px_rgba(0,0,0,0.35)]"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={2} />
        <span className="font-mono text-[11px] uppercase tracking-[0.22em]">
          Retour
        </span>
      </button>

      <button
        type="button"
        data-testid="project-topbar-next"
        onClick={nextProject}
        aria-label="Voir un autre projet"
        className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-black/70 backdrop-blur-lg border border-white/12 px-4 py-2.5 text-white hover:bg-[var(--accent)] hover:text-black hover:border-[var(--accent)] transition-all duration-300 shadow-[0_8px_30px_rgba(0,0,0,0.35)]"
      >
        <span className="font-mono text-[11px] uppercase tracking-[0.22em]">
          Voir un autre projet
        </span>
        <ArrowRight className="w-4 h-4" strokeWidth={2} />
      </button>
    </motion.div>
  );
}
